import { Box, Chip, Paper } from '@mui/material';
import { DataGridProProps, GRID_DETAIL_PANEL_TOGGLE_FIELD, GridRowParams } from '@mui/x-data-grid-pro';
import { t } from 'i18next';
import { ExpandCell } from '../../components/Cells/ExpandCell';
import { FilterConfiguration } from '../../components/FilterPanel/types';
import { TypographyOverflow } from '../../components/TypographyOverflow';
import { PaginatedTableController, PaginatedTableState } from '../../controllers/PaginatedTableController';
import { FieldConfiguration } from '../../types/paginated-table';
import { BLANK_PAGINATION_RESULT, CursorInfo, PaginationResult } from '../../types/pagination';
import { DateUtils } from '../../utils/DateUtils';
import { bind } from '../../utils/decorators';
import { SshHandshakeDetail } from '../components/SshHandshakeDetail';
import { getHandshakeStatusLabel } from '../enums/HandshakeStatus';
import { SshHandshake } from '../models/SshHandshake';

export type Props = {};

export type State = PaginatedTableState & {
  openHandshakesRowIds: number[];
};

export class SshHandshakesTableController<P extends Props = Props> extends PaginatedTableController<SshHandshake, P, State> {
  getBaseState(): State {
    return {
      ...super.getBaseState(),
      openHandshakesRowIds: [],
    };
  }

  protected async fetchData(_: CursorInfo): Promise<PaginationResult<SshHandshake>> {
    return BLANK_PAGINATION_RESULT;
  }

  protected enableSorting(): boolean {
    return true;
  }

  protected getFieldConfigurations(): FieldConfiguration<SshHandshake>[] {
    const { openHandshakesRowIds } = this.state;

    return [
      {
        fieldKey: GRID_DETAIL_PANEL_TOGGLE_FIELD,
        label: '',
        columnProps: { maxWidth: 52, minWidth: 52 },
        render: (item, { tabIndex }) => (
          <ExpandCell
            tabIndex={tabIndex}
            expanded={openHandshakesRowIds.includes(item.getId())}
            disabled={false}
            onToggle={() => this.toggleHandshake(item)}
            openLabel={t('a11y.report.handshakes.expandRow', { id: item.getId() }) as string}
            closeLabel={t('a11y.report.handshakes.collapseRow', { id: item.getId() }) as string}
          />
        ),
      },
      {
        fieldKey: 'sourceIp',
        label: t('handshakes.sourceIp'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'initiator_ip' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getInitiatorIp()}</TypographyOverflow>,
      },
      {
        fieldKey: 'targetIp',
        label: t('handshakes.targetIp'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'target_ip' },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getTargetIp()}</TypographyOverflow>,
      },
      {
        fieldKey: 'sourcePort',
        label: t('handshakes.sourcePort'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'initiator_port' },
        columnProps: { maxWidth: 150 },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getInitiatorPort()}</TypographyOverflow>,
      },
      {
        fieldKey: 'targetPort',
        label: t('handshakes.targetPort'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'target_port' },
        columnProps: { maxWidth: 150 },
        render: (item, { hasFocus }) => <TypographyOverflow hasFocus={hasFocus}>{item.getTargetPort()}</TypographyOverflow>,
      },
      {
        fieldKey: 'timestamp',
        label: t('handshakes.timestamp'),
        sortable: this.enableSorting(),
        gqlKeys: { sorterField: 'timestamp' },
        columnProps: { minWidth: 230 },
        render: (item, { hasFocus }) => {
          const timestamp = item.getTimestamp();
          if (timestamp) {
            return <TypographyOverflow hasFocus={hasFocus}>{DateUtils.toTimestampString(timestamp)}</TypographyOverflow>;
          }
          return t('handshakes.emptyDate');
        },
      },
      {
        fieldKey: 'status',
        label: t('handshakes.status'),
        sortable: false,
        render: (item) => <Chip label={getHandshakeStatusLabel(item.getStatus())} />,
      },
    ];
  }

  protected getFilterConfigurations(): FilterConfiguration[] {
    return [];
  }

  protected getAdditionalDataGridProps(): Partial<DataGridProProps<SshHandshake>> {
    const { openHandshakesRowIds } = this.state;
    return {
      ...super.getAdditionalDataGridProps(),

      // Force the table to update when necessary
      nonce: openHandshakesRowIds.join('_'),

      // Allow the expanded row to display all content
      getDetailPanelHeight: () => 'auto',

      // Control which rows are expanded based on internal state
      detailPanelExpandedRowIds: openHandshakesRowIds,

      // Render the expanded row
      getDetailPanelContent: this.renderHandshakeDetails,
    };
  }

  @bind
  private toggleHandshake(handshake: SshHandshake) {
    const { openHandshakesRowIds } = this.state;
    const rowId = handshake.getId();
    if (openHandshakesRowIds.includes(rowId)) {
      this.setState({ openHandshakesRowIds: openHandshakesRowIds.filter((p) => p !== rowId) });
    } else {
      this.setState({ openHandshakesRowIds: [...openHandshakesRowIds, rowId] });
    }
  }

  @bind
  private renderHandshakeDetails({ row }: GridRowParams<SshHandshake>) {
    return (
      <Box sx={{ p: 2, backgroundColor: 'backdrops.expandedRow' }}>
        <Paper>
          <SshHandshakeDetail handshake={row} />
        </Paper>
      </Box>
    );
  }
}
